import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { uploadOne, fileUrl } from "@/lib/uploads";
import AssignmentActions from "@/components/common/AssignmentActions";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";
import {
  ClipboardCheck, Camera, Send, CheckCircle2, XCircle, Copy, ExternalLink,
  ChevronRight, Loader2, Layers, Sparkles,
} from "lucide-react";

const CATEGORIES = [
  { key: "aesthetic", label: "Aesthetic" },
  { key: "functional", label: "Functional" },
  { key: "structural", label: "Structural" },
];

const TRADE_TAGS = ["Painter", "Carpenter", "Electrician", "Plumber", "Polisher", "Cleaner", "Other"];

function ProgressGauge({ percent }) {
  return (
    <div className="w-full h-2 rounded-full bg-[#1c1c1c] overflow-hidden">
      <div
        className="h-full transition-all"
        style={{
          width: `${percent}%`,
          background: "linear-gradient(90deg, #d4af37 0%, #f2d478 100%)",
        }}
      />
    </div>
  );
}

function TabButton({ active, children, testid, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-[11px] font-mono uppercase tracking-[0.25em] border-b-2 transition-all ${
        active ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-gray-500 hover:text-gray-300"
      }`}
    >
      {children}
    </button>
  );
}

export default function SnagCRMPage() {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [tab, setTab] = useState("walkthrough");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // Feb 2026 · city filter (CRM Executive is city-scoped by backend already)
  const [cityFilter, setCityFilter] = useState("all");
  const [myCities, setMyCities] = useState([]);
  const [unrestricted, setUnrestricted] = useState(true);

  useEffect(() => {
    api.get("/me/assigned-cities")
      .then(({ data }) => {
        setMyCities(data.assigned_cities || []);
        setUnrestricted(!!data.unrestricted);
      })
      .catch(() => {});
  }, []);

  const filteredProjects = useMemo(() => {
    let base = projects;
    if (cityFilter !== "all") {
      const cf = cityFilter.toLowerCase();
      base = base.filter((p) => (p.city || "").toLowerCase() === cf);
    }
    return base.filter((p) => projectMatches(p, q));
  }, [projects, q, cityFilter]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/snags/projects");
      setProjects(data);
      // First-select initial project — driven off the *latest* selectedId via
      // functional setState so the callback doesn't need selectedId in its
      // dep list (otherwise every click refetches the whole project list).
      setSelectedId((prev) => (prev || (data.length ? data[0].id : null)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.get(`/snags/projects/${selectedId}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [selectedId]);

  const loadSups = useCallback(async () => {
    try {
      const { data } = await api.get("/users/by-role/Site%20Supervisor");
      setSupervisors(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); loadSups(); }, [load, loadSups]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Auto-refresh the project list + open detail every 25s so CRM sees new
  // resolved-snags without having to reload manually.
  useEffect(() => {
    const t = setInterval(() => { load(); loadDetail(); }, 25000);
    return () => clearInterval(t);
  }, [load, loadDetail]);

  const resolvedSnags = useMemo(
    () => (selected?.snags || []).filter((s) => s.status === "resolved"),
    [selected]
  );
  const openSnags = useMemo(
    () => (selected?.snags || []).filter((s) => s.status === "open" || s.status === "in_progress"),
    [selected]
  );
  const verifiedSnags = useMemo(
    () => (selected?.snags || []).filter((s) => s.status === "verified"),
    [selected]
  );

  return (
    <div className="p-8 min-h-screen">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-1">Phase 8 · Snag Resolution & QA</p>
          <h1 className="font-display text-3xl text-white">CRM Snag Workspace</h1>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" strokeWidth={1.5} />
          Continuous walkthrough → resolution → verification → auto settlement
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Project Selector */}
        <aside className="h-fit">
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            resultCount={filteredProjects.length}
            totalCount={projects.length}
            testid="snag-crm-search"
          />
          {!unrestricted && myCities.length > 0 && (
            <div className="mb-3" data-testid="crm-city-filter-container">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">
                City ({myCities.length} assigned)
              </label>
              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="iz-input w-full text-sm"
                data-testid="crm-city-filter"
              >
                <option value="all">All Assigned Cities</option>
                {myCities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}
          <div className="border border-[#262626] bg-[#0a0a0a] rounded-md">
            <p className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 border-b border-[#262626]">
              Projects
            </p>
            {projects.length === 0 && (
              <p className="p-6 text-xs text-gray-500">No site projects available yet.</p>
            )}
            {projects.length > 0 && filteredProjects.length === 0 && q.trim() && (
              <div className="p-4">
                <ProjectSearchEmpty query={q} testid="snag-crm-search-empty" />
              </div>
            )}
            {filteredProjects.map((p) => {
            const s = p.snag_summary || { total: 0, verified: 0, completion_percent: 0 };
            const isActive = p.id === selectedId;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                data-testid={`snag-project-${p.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#1c1c1c] transition-all ${
                  isActive ? "bg-[#d4af37]/5 border-l-2 border-l-[#d4af37]" : "hover:bg-[#131313]"
                }`}
              >
                <p className="text-sm text-white truncate">{p.client_name} · {p.city || "—"}</p>
                <p className="text-[11px] font-mono text-gray-500 truncate">
                  {p.project_name}
                </p>
                <div className="mt-2">
                  <ProgressGauge percent={s.completion_percent} />
                  <p className="text-[10px] font-mono text-gray-500 mt-1">
                    {s.verified}/{s.total} verified · {s.completion_percent}%
                  </p>
                </div>
              </button>
            );
          })}
          </div>
        </aside>

        {/* Main Panel */}
        <section className="border border-[#262626] bg-[#0a0a0a] rounded-md min-h-[70vh]">
          {!selected && (
            <div className="p-12 text-center text-xs text-gray-500">Select a project on the left.</div>
          )}

          {selected && (
            <>
              <div className="p-6 border-b border-[#262626] flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
                    {selected.status?.replace(/_/g, " ")}
                  </p>
                  <h2 className="font-display text-2xl text-white mt-1">{selected.client_name}{selected.city ? ` · ${selected.city}` : ""}</h2>
                  <p className="text-xs text-gray-500 mt-1">{selected.project_name}</p>
                </div>
                <div className="min-w-[240px]">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">
                    Overall Snag Resolution
                  </p>
                  <ProgressGauge percent={selected.snag_summary?.completion_percent ?? 0} />
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-between">
                    <span>{selected.snag_summary?.verified}/{selected.snag_summary?.total} verified</span>
                    <span className="text-[#d4af37] font-mono">{selected.snag_summary?.completion_percent}%</span>
                  </p>
                  <PublicLinkRow project={selected} />
                  {selected.settlement_sent_at && (
                    <div className="mt-3 border border-[#d4af37]/40 bg-[#d4af37]/5 px-3 py-2 rounded text-[11px] text-[#d4af37]" data-testid="settlement-badge">
                      💼 Final settlement dispatched
                      <span className="text-gray-500 ml-2 font-mono">
                        {new Date(selected.settlement_sent_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-b border-[#262626] flex px-6">
                <TabButton active={tab === "walkthrough"} testid="tab-walkthrough" onClick={() => setTab("walkthrough")}>
                  1 · Walkthrough
                </TabButton>
                <TabButton active={tab === "verify"} testid="tab-verify" onClick={() => setTab("verify")}>
                  2 · Verify ({resolvedSnags.length})
                </TabButton>
                <TabButton active={tab === "ledger"} testid="tab-ledger" onClick={() => setTab("ledger")}>
                  Ledger
                </TabButton>
              </div>

              <div className="p-6">
                {tab === "walkthrough" && (
                  <WalkthroughForm
                    projectId={selected.id}
                    supervisors={supervisors}
                    onCreated={() => { loadDetail(); load(); }}
                    openSnags={openSnags}
                  />
                )}
                {tab === "verify" && (
                  <VerifyList
                    items={resolvedSnags}
                    onChange={() => { loadDetail(); load(); }}
                  />
                )}
                {tab === "ledger" && (
                  <SnagLedger
                    open={openSnags}
                    resolved={resolvedSnags}
                    verified={verifiedSnags}
                  />
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function PublicLinkRow({ project }) {
  const publicUrl = project.public_snag_token
    ? `${window.location.origin}/client-progress/${project.public_snag_token}`
    : null;
  const copy = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    toast.success("Client viewport link copied");
  };
  if (!publicUrl) return null;
  return (
    <div className="mt-3 flex items-center gap-2 text-[10px] text-gray-500">
      <button onClick={copy} data-testid="copy-public-link" className="hover:text-[#d4af37] flex items-center gap-1">
        <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy client link
      </button>
      <a href={publicUrl} target="_blank" rel="noreferrer" className="hover:text-[#d4af37] flex items-center gap-1" data-testid="open-public-link">
        <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> Open
      </a>
    </div>
  );
}

function WalkthroughForm({ projectId, supervisors, onCreated, openSnags }) {
  const fileRef = useRef(null);
  const [beforeFile, setBeforeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    category: "aesthetic",
    description: "",
    assigned_supervisor_id: "",
    target_completion_date: "",
    trade_resource_tag: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const pickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const rec = await uploadOne(f);
      setBeforeFile(rec);
      toast.success("Before-photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Description required");
    if (!form.assigned_supervisor_id) return toast.error("Select a supervisor");
    if (!form.target_completion_date) return toast.error("Set a target date");
    setSubmitting(true);
    try {
      await api.post(`/snags/projects/${projectId}/walkthrough`, {
        category: form.category,
        description: form.description.trim(),
        assigned_supervisor_id: form.assigned_supervisor_id,
        target_completion_date: form.target_completion_date,
        trade_resource_tag: form.trade_resource_tag || null,
        before_photo_file_id: beforeFile?.id || null,
      });
      toast.success("Snag logged & dispatched");
      setForm({ ...form, description: "", target_completion_date: "", trade_resource_tag: "" });
      setBeforeFile(null);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Log failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="walkthrough-form">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Category</label>
          <select
            className="iz-input w-full"
            data-testid="snag-category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Trade tag (optional)</label>
          <select
            className="iz-input w-full"
            data-testid="snag-trade"
            value={form.trade_resource_tag}
            onChange={(e) => setForm({ ...form, trade_resource_tag: e.target.value })}
          >
            <option value="">— None —</option>
            {TRADE_TAGS.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Description</label>
          <textarea
            className="iz-input w-full min-h-[70px]"
            data-testid="snag-description"
            placeholder="e.g. Scuff mark on kitchen shutter — needs polish"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Assign Supervisor</label>
          <select
            className="iz-input w-full"
            data-testid="snag-supervisor"
            value={form.assigned_supervisor_id}
            onChange={(e) => setForm({ ...form, assigned_supervisor_id: e.target.value })}
          >
            <option value="">— Select —</option>
            {supervisors.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 block mb-1">Target date</label>
          <input
            type="date"
            className="iz-input w-full"
            data-testid="snag-target-date"
            value={form.target_completion_date}
            onChange={(e) => setForm({ ...form, target_completion_date: e.target.value })}
          />
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={pickPhoto}
            className="hidden"
            data-testid="before-photo-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="iz-btn-secondary flex items-center gap-2 text-xs"
            data-testid="before-photo-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {beforeFile ? "Replace before-photo" : "Attach before-photo (optional)"}
          </button>
          {beforeFile && (
            <a href={fileUrl(beforeFile.id)} target="_blank" rel="noreferrer" className="text-[11px] text-[#d4af37] underline">
              Preview
            </a>
          )}
          <div className="flex-1" />
          <button
            type="submit"
            disabled={submitting}
            data-testid="snag-log-btn"
            className="iz-btn-primary flex items-center gap-2 text-xs disabled:opacity-40"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />}
            Log Snag Item
          </button>
        </div>
      </form>

      <div className="border-t border-[#1c1c1c] pt-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Open Items ({openSnags.length})</p>
        {openSnags.length === 0 && <p className="text-xs text-gray-500">No open items.</p>}
        <ul className="space-y-2">
          {openSnags.map((s) => (
            <li key={s.id} className="border border-[#1c1c1c] bg-[#0f0f0f] rounded-md px-4 py-3 flex items-center justify-between gap-4 text-xs" data-testid={`open-snag-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-white truncate">{s.title}</p>
                <p className="text-[10px] text-gray-500 mt-0.5 font-mono uppercase tracking-widest">
                  {s.category} · {s.assigned_supervisor_name || "Unassigned"} · due {s.target_completion_date}
                </p>
              </div>
              {s.reject_count > 0 && (
                <span className="text-[10px] font-mono text-red-400">↩ {s.reject_count} rejects</span>
              )}
              <AssignmentActions
                itemLabel={s.title}
                reassignEndpoint={`/snags/${s.id}/reassign-supervisor`}
                assigneeKey="assigned_supervisor_id"
                candidates={supervisors}
                onDone={onCreated}
                canSelfAssign={false}
                testIdPrefix={`snag-${s.id}`}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function VerifyList({ items, onChange }) {
  if (items.length === 0) {
    return <p className="text-xs text-gray-500 py-8 text-center">Nothing awaiting verification.</p>;
  }
  return (
    <ul className="space-y-3" data-testid="verify-list">
      {items.map((s) => <VerifyRow key={s.id} snag={s} onChange={onChange} />)}
    </ul>
  );
}

function VerifyRow({ snag, onChange }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (approve) => {
    if (!approve && !comment.trim()) return toast.error("Rejection comment required");
    setBusy(true);
    try {
      await api.patch(`/snags/${snag.id}/verify`, { approve, comment: comment.trim() || null });
      toast.success(approve ? "Snag verified ✓" : "Snag reopened");
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="border border-[#262626] bg-[#0f0f0f] rounded-md p-4" data-testid={`verify-row-${snag.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white">{snag.title}</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
            {snag.category} · {snag.trade_resource_tag || "—"} · Sup: {snag.assigned_supervisor_name || "—"}
          </p>
          <p className="text-xs text-gray-400 mt-2">{snag.description}</p>
          <div className="flex gap-3 mt-3">
            {snag.before_photo_file_id && (
              <a href={fileUrl(snag.before_photo_file_id)} target="_blank" rel="noreferrer"
                 className="text-[10px] text-gray-400 underline hover:text-[#d4af37]">Before ↗</a>
            )}
            {snag.after_photo_file_id && (
              <a href={fileUrl(snag.after_photo_file_id)} target="_blank" rel="noreferrer"
                 className="text-[10px] text-[#d4af37] underline">After ↗</a>
            )}
          </div>
        </div>
        <div className="w-64">
          <textarea
            className="iz-input w-full min-h-[60px] text-xs"
            placeholder="Comment (required if rejecting)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid={`verify-comment-${snag.id}`}
          />
          <div className="flex gap-2 mt-2">
            <button
              disabled={busy}
              onClick={() => act(true)}
              data-testid={`verify-approve-${snag.id}`}
              className="iz-btn-primary flex-1 text-[11px] flex items-center justify-center gap-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Verify
            </button>
            <button
              disabled={busy}
              onClick={() => act(false)}
              data-testid={`verify-reject-${snag.id}`}
              className="iz-btn-secondary flex-1 text-[11px] flex items-center justify-center gap-1"
            >
              <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Reopen
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

function SnagLedgerSection({ title, items, tone }) {
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">
        {title} <span className="text-[#d4af37]">· {items.length}</span>
      </p>
      <ul className="space-y-1.5">
        {items.length === 0 && <li className="text-xs text-gray-600">—</li>}
        {items.map((s) => (
          <li key={s.id} className="border border-[#1c1c1c] bg-[#0f0f0f] rounded px-3 py-2 text-xs flex items-center justify-between">
            <div>
              <p className="text-gray-200 truncate">{s.title}</p>
              <p className="text-[10px] text-gray-500 mt-0.5 font-mono uppercase tracking-widest">
                {s.category} · {s.assigned_supervisor_name || "—"}
              </p>
            </div>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
              tone === "gold" ? "text-[#d4af37] border border-[#d4af37]/40" :
              tone === "green" ? "text-emerald-400 border border-emerald-500/40" :
              "text-gray-500 border border-gray-700"
            }`}>
              {s.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SnagLedger({ open, resolved, verified }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="snag-ledger">
      <SnagLedgerSection title="Open" items={open} tone="gray" />
      <SnagLedgerSection title="Resolved · Awaiting" items={resolved} tone="gold" />
      <SnagLedgerSection title="Verified" items={verified} tone="green" />
    </div>
  );
}
